import { CreateAccountOp, Operation, OperationBody } from "../../xdr/index.js";
import { Keypair } from "../keypair.js";
import { StrKey } from "../strkey.js";
import { CreateAccountOpts, OperationAttributes } from "./types.js";
import {
  constructAmountRequirementsError,
  isValidAmount,
  setSourceAccount,
  toXdrAmount,
} from "../util/operations.js";

/**
 * Create and fund a non-existent account.
 *
 * @param opts - Options object
 *   - `destination`: Destination account ID to create an account for.
 *   - `startingBalance`: Amount in XLM the account should be funded for. Must be greater
 *     than the {@link https://developers.stellar.org/docs/glossary/fees/ | reserve balance amount}.
 *   - `source`: The source account for the payment. Defaults to the transaction's source account.
 */
export function createAccount(opts: CreateAccountOpts): Operation {
  if (!StrKey.isValidEd25519PublicKey(opts.destination)) {
    throw new Error("destination is invalid");
  }
  if (!isValidAmount(opts.startingBalance, true)) {
    throw new TypeError(constructAmountRequirementsError("startingBalance"));
  }

  const createAccountOp = new CreateAccountOp({
    destination: Keypair.fromPublicKey(opts.destination).xdrAccountId(),
    startingBalance: toXdrAmount(opts.startingBalance),
  });

  const opAttributes: OperationAttributes = {
    sourceAccount: null,
    body: OperationBody.createAccount(createAccountOp),
  };
  setSourceAccount(opAttributes, opts);

  return new Operation(opAttributes);
}
